import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toastWarning, getTTSLanguageCode } from '../../../../../utils/utils';
import { tUi } from '../../../../../utils/dq_lc';
import { highlightWordRenderer as pureHighlightWordRenderer, stripHtmlForHighlight } from '../../utils/LearnTab/ttsHighlight.utils';

export const usePresentationTTS = ({
    nav,
    data,
    currentSessionSlide = nav?.currentSessionSlide,
    currentSegmentSlides = nav?.currentSegmentSlides ?? [],
    currentSlide = nav?.currentSlide ?? 0,
    currentDisplayLanguage = data?.currentDisplayLanguage ?? 'en-US',
    selectedSession = data?.selectedSession,
    sessionAssessments = data?.sessionAssessments ?? {},
    activeSegment = nav?.activeSegment,
    uiLocale = data?.uiLocale ?? 'en-US',
}) => {
    // States for native TTS
    const [speaking, setSpeaking] = useState(false);
    const [paused, setPaused] = useState(false);
    const [currentWordIndex, setCurrentWordIndex] = useState(-1);
    const [ttsAuto, setTtsAuto] = useState(false);

    // TTS Settings State
    const [ttsRate, setTtsRate] = useState(1);
    const [ttsPitch, setTtsPitch] = useState(1);
    const [ttsVolume, setTtsVolume] = useState(1);
    const [availableVoices, setAvailableVoices] = useState([]);
    const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
    const [ttsSettingsAnchor, setTtsSettingsAnchor] = useState(null);

    const utteranceRef = useRef(null);
    const speechWordsRef = useRef([]); // array of words (strings)
    const speechPartRangesRef = useRef({}); // id -> { start, end }
    const lastSpokenSlideTitleRef = useRef(null);

    const TTS_TOAST_WARNING = tUi('preview.toast.ttsBrowser', uiLocale);

    // Derive TTS language from current display language
    const ttsLang = getTTSLanguageCode(currentDisplayLanguage ? currentDisplayLanguage.split('-')[0] : 'en');

    // Load available voices filtered to current language
    useEffect(() => {
        const loadVoices = () => {
            try {
                const vs = window.speechSynthesis?.getVoices?.() || [];
                const baseLang = ttsLang.split('-')[0].toLowerCase();
                const filteredVoices = vs.filter(voice => {
                    const voiceLang = (voice.lang || '').toLowerCase();
                    return voiceLang.startsWith(baseLang);
                });
                setAvailableVoices(filteredVoices);
            } catch (e) {
                setAvailableVoices([]);
            }
        };
        loadVoices();
        if (window && window.speechSynthesis) {
            window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
        }
        return () => {
            if (window && window.speechSynthesis) {
                window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
            }
        };
    }, [ttsLang]);

    // Reset voice selection when language changes
    useEffect(() => {
        setSelectedVoiceURI('');
    }, [ttsLang]);

    // Determine if we should skip title speaking (consecutive same titles)
    const skipTitleSpeech = useMemo(() => {
        if (!currentSegmentSlides || currentSegmentSlides.length === 0) return false;
        const slide = currentSegmentSlides[currentSlide];
        const title = slide?.SlideTitle || '';
        const prevSlide = currentSlide > 0 ? currentSegmentSlides[currentSlide - 1] : null;
        return prevSlide ? prevSlide.SlideTitle === title : lastSpokenSlideTitleRef.current === title;
    }, [currentSegmentSlides, currentSlide]);

    // Build speech parts and word mapping
    const prepareSpeechForSlide = useCallback((slide) => {
        speechWordsRef.current = [];
        speechPartRangesRef.current = {};
        if (!slide) return '';

        const stripHtmlForTTS = (text) => {
            if (typeof text !== 'string') return String(text || '');
            return text
                .replace(/<br\s*\/?>/gi, ' ')          // Replace <br> with space
                .replace(/<[^>]+>/g, '')               // Remove all HTML tags
                .replace(/&nbsp;/gi, ' ')              // Replace &nbsp; with space
                .replace(/\s+/g, ' ')                  // Collapse multiple spaces
                .trim();
        };

        // Handle assessment slides
        if (slide.kind === 'ASSESSMENT') {
            const parts = [];
            const sessKey = selectedSession?.session_code;
            const assess = sessionAssessments?.[sessKey] || {};
            const assessmentItems = activeSegment === '__PRE_ASSESSMENT__' ? (assess.pre || []) : (assess.post || []);
            const questionData = assessmentItems[slide.questionIndex];

            if (questionData) {
                if (questionData.type === 'assessment_intro') {
                    if (questionData.title) {
                        const cleaned = stripHtmlForTTS(questionData.title);
                        if (cleaned) parts.push({ id: 'intro_title', text: cleaned + '.' });
                    }
                    if (questionData.content) {
                        const cleaned = stripHtmlForTTS(questionData.content);
                        if (cleaned) parts.push({ id: 'intro_content', text: cleaned + '.' });
                    }
                } else {
                    if (questionData.question_subtitle_top) {
                        const cleaned = stripHtmlForTTS(questionData.question_subtitle_top);
                        if (cleaned) parts.push({ id: 'subtitle_top', text: cleaned + '.' });
                    }
                    if (questionData.instrument_question) {
                        const cleaned = stripHtmlForTTS(questionData.instrument_question);
                        if (cleaned) parts.push({ id: 'question', text: cleaned + '.' });
                    }
                    if (questionData.question_subtitle_bottom) {
                        const cleaned = stripHtmlForTTS(questionData.question_subtitle_bottom);
                        if (cleaned) parts.push({ id: 'subtitle_bottom', text: cleaned + '.' });
                    }

                    let options = [];
                    try {
                        options = typeof questionData.instrument_response === 'string'
                            ? JSON.parse(questionData.instrument_response)
                            : questionData.instrument_response;
                        if (!Array.isArray(options)) options = [options];
                    } catch (e) {
                        options = [];
                    }

                    if (options && options.length > 0) {
                        options.forEach((opt, idx) => {
                            const cleaned = stripHtmlForTTS(opt);
                            if (cleaned) parts.push({ id: `option_${idx}`, text: cleaned });
                        });
                    }
                }
            }

            let globalWordIndex = 0;
            parts.forEach(p => {
                const words = p.text.match(/[^\s]+/g) || [];
                const start = globalWordIndex;
                const end = start + words.length - 1;
                speechPartRangesRef.current[p.id] = { start, end };
                speechWordsRef.current.push(...words);
                globalWordIndex = end + 1;
            });
            return parts.map(p => p.text).join('. ');
        }

        // Handle regular slides
        const parts = [];
        if (slide.SlideTitle && !skipTitleSpeech) {
            const cleanedTitle = stripHtmlForTTS(slide.SlideTitle);
            if (cleanedTitle) parts.push({ id: 'title', text: cleanedTitle + '.' });
        }
        const pushContentString = (text, idBase, isSubHeading = false) => {
            if (typeof text === 'string' && text.trim().length) {
                const plainText = stripHtmlForTTS(text);
                if (!plainText.length) return;

                const processedText = isSubHeading ? plainText + '.' : plainText;
                parts.push({ id: idBase, text: processedText });
            }
        };
        const sc = slide.SlideContent;
        if (typeof sc === 'string') {
            pushContentString(sc, 'content_0');
        } else if (Array.isArray(sc)) {
            sc.forEach((item, idx) => {
                if (typeof item === 'string') {
                    pushContentString(item, `content_${idx}`);
                } else if (item && typeof item === 'object') {
                    if (item.Title) pushContentString(item.Title, `content_${idx}_title`, true);
                    if (item.Text) pushContentString(item.Text, `content_${idx}_text`);
                }
            });
        } else if (sc && typeof sc === 'object') {
            Object.entries(sc).forEach(([k, v]) => {
                if (typeof v === 'string') pushContentString(v, `content_obj_${k}`);
            });
        }

        let globalWordIndex = 0;
        parts.forEach(p => {
            const words = p.text.match(/[^\s]+/g) || [];
            const start = globalWordIndex;
            const end = start + words.length - 1;
            speechPartRangesRef.current[p.id] = { start, end };
            speechWordsRef.current.push(...words);
            globalWordIndex = end + 1;
        });

        return parts.map(p => p.text).join('. ');
    }, [skipTitleSpeech, sessionAssessments, selectedSession, activeSegment]);

    const speechText = useMemo(() => prepareSpeechForSlide(currentSessionSlide), [currentSessionSlide, prepareSpeechForSlide]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (window?.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    const startSpeech = useCallback(() => {
        if (!speechText) return;

        if (!('speechSynthesis' in window) || !window.speechSynthesis) {
            toastWarning(TTS_TOAST_WARNING);
            return;
        }

        window.speechSynthesis.cancel();

        const utter = new SpeechSynthesisUtterance(speechText);
        utter.lang = ttsLang;
        utter.rate = ttsRate;
        utter.pitch = ttsPitch;
        utter.volume = ttsVolume;

        let voices;
        try {
            const vs = window.speechSynthesis?.getVoices?.() || [];
            const baseLang = ttsLang.split('-')[0].toLowerCase();
            voices = vs.filter(voice => {
                const voiceLang = (voice.lang || '').toLowerCase();
                return voiceLang.startsWith(baseLang);
            });
        } catch (e) {
            voices = [];
        }

        if (!voices || voices.length === 0) {
            setTimeout(() => {
                const vs = window.speechSynthesis?.getVoices?.() || [];
                const baseLang = ttsLang.split('-')[0].toLowerCase();
                const retryVoices = vs.filter(voice => (voice.lang || '').toLowerCase().startsWith(baseLang));
                if (retryVoices.length === 0) return;
                startSpeech();
            }, 500);
            return;
        }

        if (voices.length > 0) {
            let chosen = null;
            if (selectedVoiceURI) {
                chosen = voices.find(v => v.voiceURI === selectedVoiceURI) || null;
            }
            if (!chosen) {
                const femaleVoice = voices.find(voice => {
                    const name = (voice.name || '').toLowerCase();
                    return (
                        name.includes('female') ||
                        name.includes('zira') || name.includes('susan') || name.includes('samantha') ||
                        name.includes('karen') || name.includes('anna') || name.includes('marie') ||
                        name.includes('serena') || name.includes('alexia') || name.includes('tessa') ||
                        name.includes('alice') || name.includes('victoria') || name.includes('amelie') ||
                        name.includes('ellen') || name.includes('kyoko') || name.includes('sayaka') ||
                        name.includes('yuna') || name.includes('ayumi') || name.includes('premwadee')
                    );
                });
                chosen = femaleVoice || null;
                if (!chosen) {
                    chosen = voices.length >= 2 ? voices[1] : voices[0];
                }
            }
            if (chosen) {
                utter.voice = chosen;
            }
        }

        setCurrentWordIndex(-1);
        speechWordsRef.current = speechText.match(/[^\s]+/g) || [];

        utter.onstart = () => {
            setSpeaking(true);
            setPaused(false);
        };

        utter.onboundary = (e) => {
            if (e.name === 'word' || e.charIndex != null) {
                if (e.charIndex != null) {
                    let count = 0;
                    const textUpToChar = speechText.slice(0, e.charIndex);
                    const arr = textUpToChar.match(/[^\s]+/g);
                    if (arr) count = arr.length;
                    setCurrentWordIndex(count);
                }
            }
        };

        utter.onend = () => {
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
            if (!skipTitleSpeech && currentSessionSlide?.SlideTitle) {
                lastSpokenSlideTitleRef.current = currentSessionSlide.SlideTitle;
            }
        };

        utter.onerror = (event) => {
            console.error('Speech synthesis error:', event);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
        };

        utteranceRef.current = utter;

        try {
            window.speechSynthesis.speak(utter);

            setTimeout(() => {
                if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
                    setSpeaking(false);
                    setPaused(false);
                    setCurrentWordIndex(-1);
                }
            }, 250);
        } catch (error) {
            console.error('Failed to start speech synthesis:', error);
            setSpeaking(false);
            setPaused(false);
            setCurrentWordIndex(-1);
        }
    }, [speechText, currentSessionSlide, skipTitleSpeech, ttsRate, ttsPitch, ttsVolume, selectedVoiceURI, ttsLang, TTS_TOAST_WARNING]);

    const pauseSpeech = useCallback(() => {
        if (!speaking) return;
        if (!paused) {
            window.speechSynthesis.pause();
            setPaused(true);
        } else {
            window.speechSynthesis.resume();
            setPaused(false);
        }
    }, [speaking, paused]);

    const stopSpeech = useCallback(() => {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        setPaused(false);
        setCurrentWordIndex(-1);
    }, []);

    // Handle auto-play
    useEffect(() => {
        if (ttsAuto) {
            stopSpeech();
            if (speechText) {
                startSpeech();
            }
        } else {
            stopSpeech();
        }
    }, [ttsAuto, currentSessionSlide, speechText, startSpeech, stopSpeech]);

    // Restart speech if settings change while speaking with auto-play
    useEffect(() => {
        if (ttsAuto && speaking) {
            stopSpeech();
            startSpeech();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ttsRate, ttsPitch, ttsVolume, selectedVoiceURI]);

    // Highlight helper passed to slide component
    const highlightWordRenderer = useCallback((id, rawText) => {
        return pureHighlightWordRenderer(id, rawText, currentWordIndex, speechPartRangesRef.current);
    }, [currentWordIndex]);

    return {
        speaking,
        paused,
        currentWordIndex,
        ttsAuto,
        setTtsAuto,
        ttsRate,
        setTtsRate,
        ttsPitch,
        setTtsPitch,
        ttsVolume,
        setTtsVolume,
        availableVoices,
        selectedVoiceURI,
        setSelectedVoiceURI,
        ttsSettingsAnchor,
        setTtsSettingsAnchor,
        startSpeech,
        pauseSpeech,
        stopSpeech,
        highlightWordRenderer,
        skipTitleSpeech,
        speechText,
        lastSpokenSlideTitleRef,
    };
};

export default usePresentationTTS;
